import { dueBucket, type Contact, type DueBucket, type Person, type SchemaOptions } from '@crmsy/shared';
import { t, type Key } from '../i18n/index.js';

/**
 * Grouping (D-38): Table and Follow-ups can nest their rows under up to three fields,
 * e.g. Status › Stage › Owner. The choice lives in the URL (`group=status,stage`) next to
 * the filters, so a grouped view is a shareable link; the bar's open/closed state and
 * collapsed groups are per device / per session. Filters narrow, grouping arranges:
 * `x` (clear filters) leaves grouping alone.
 */
export type GroupKey = 'due' | 'stage' | 'status' | 'nextAction' | 'owner' | 'leadSource' | 'industry' | 'contactRole';

export const GROUP_KEYS: GroupKey[] = ['due', 'stage', 'status', 'nextAction', 'owner', 'leadSource', 'industry', 'contactRole'];
export const MAX_GROUP_LEVELS = 3;

const PARAM: Record<GroupKey, string> = { due: 'due', stage: 'stage', status: 'status', nextAction: 'action', owner: 'owner', leadSource: 'source', industry: 'industry', contactRole: 'role' };
const KEY_OF_PARAM = Object.fromEntries(Object.entries(PARAM).map(([k, v]) => [v, k])) as Record<string, GroupKey>;

/** Notion property behind a select-like group key; `due` and `owner` are derived. */
export const GROUP_PROPERTY: Partial<Record<GroupKey, string>> = { stage: 'Stage', status: 'Status', nextAction: 'Next Action', leadSource: 'Lead Source', industry: 'Industry', contactRole: 'Contact Role' };
export const GROUP_FIELD_LABEL: Record<GroupKey, Key> = { due: 'group.due', stage: 'field.stage', status: 'field.status', nextAction: 'field.nextAction', owner: 'field.owner', leadSource: 'field.leadSource', industry: 'field.industry', contactRole: 'field.role' };

const DUE_ORDER: DueBucket[] = ['overdue', 'today', 'week', 'later', 'none'];
const DUE_LABEL: Record<DueBucket, Key> = { overdue: 'group.overdue', today: 'group.today', week: 'group.week', later: 'group.later', none: 'group.noDate' };

export function groupingFromSearch(sp: URLSearchParams, defaults: GroupKey[]): GroupKey[] {
  if (!sp.has('group')) return defaults;
  const out: GroupKey[] = [];
  for (const p of (sp.get('group') ?? '').split(',')) {
    const k = KEY_OF_PARAM[p];
    if (k && !out.includes(k)) out.push(k);
  }
  return out.slice(0, MAX_GROUP_LEVELS);
}

/** Writes `group=` only when the choice differs from the view's default (an empty `group=` means "none" where the default is not). */
export function groupingToSearch(sp: URLSearchParams, groups: GroupKey[], defaults: GroupKey[]): void {
  if (groups.length === defaults.length && groups.every((k, i) => k === defaults[i])) sp.delete('group');
  else sp.set('group', groups.map((k) => PARAM[k]).join(','));
}

export interface GroupContext {
  schema: SchemaOptions;
  people: Person[];
  today: string;
}

export interface GroupNode<T> {
  key: GroupKey;
  /** Raw value: option name, person id, due bucket; '' when the contact has none. */
  value: string;
  label: string;
  /** `key=value/` segments from the root; identifies the group for collapsing. */
  path: string;
  level: number;
  count: number;
  /** Inner nodes have children, leaves have items. */
  children: GroupNode<T>[];
  items: T[];
}

export function groupValue(c: Contact, key: GroupKey, today: string): string {
  if (key === 'due') return dueBucket(c.nextContactDate, today);
  if (key === 'owner') return c.owner[0] ?? '';
  return c[key] ?? '';
}

export function groupLabel(key: GroupKey, value: string, ctx: GroupContext): string {
  if (key === 'due') return t(DUE_LABEL[value as DueBucket] ?? 'group.noDate');
  if (key === 'owner') return value ? (ctx.people.find((p) => p.id === value)?.name ?? '?') : t('group.none');
  return value || t('group.none');
}

/** Sort position of a group: Notion's option order (or due order, or owner name); unknown values after, empty last. */
function rank(key: GroupKey, value: string, ctx: GroupContext): [number, string] {
  if (!value) return [Number.MAX_SAFE_INTEGER, ''];
  if (key === 'due') return [DUE_ORDER.indexOf(value as DueBucket), ''];
  if (key === 'owner') return [0, ctx.people.find((p) => p.id === value)?.name ?? '￿'];
  const i = (ctx.schema[GROUP_PROPERTY[key]!] ?? []).findIndex((o) => o.name === value);
  return i >= 0 ? [i, ''] : [1_000_000, value];
}

/** Nests `items` under `keys` (outermost first), keeping their incoming order inside each leaf. */
export function groupItems<T>(items: T[], keys: GroupKey[], contactOf: (x: T) => Contact, ctx: GroupContext): GroupNode<T>[] {
  if (!keys.length) return [];
  const build = (list: T[], level: number, parent: string): GroupNode<T>[] => {
    const key = keys[level]!;
    const buckets = new Map<string, T[]>();
    for (const it of list) {
      const v = groupValue(contactOf(it), key, ctx.today);
      const b = buckets.get(v);
      if (b) b.push(it);
      else buckets.set(v, [it]);
    }
    const ranked = [...buckets.keys()].map((v) => ({ v, r: rank(key, v, ctx) }));
    ranked.sort((a, b) => a.r[0] - b.r[0] || a.r[1].localeCompare(b.r[1]));
    return ranked.map(({ v }) => {
      const its = buckets.get(v)!;
      const path = `${parent}${key}=${v}/`;
      const leaf = level + 1 >= keys.length;
      return { key, value: v, label: groupLabel(key, v, ctx), path, level, count: its.length, children: leaf ? [] : build(its, level + 1, path), items: leaf ? its : [] };
    });
  };
  return build(items, 0, '');
}

export type FlatRow<T> = { kind: 'header'; node: GroupNode<T>; collapsed: boolean } | { kind: 'item'; item: T; path: string };

/** Depth-first rows for rendering: a header per group, then its rows unless collapsed. Ungrouped lists pass `items`. */
export function flattenGroups<T>(nodes: GroupNode<T>[], items: T[], collapsed: Set<string>): FlatRow<T>[] {
  if (!nodes.length) return items.map((item) => ({ kind: 'item', item, path: '' }));
  const out: FlatRow<T>[] = [];
  const walk = (ns: GroupNode<T>[]) => {
    for (const n of ns) {
      const c = collapsed.has(n.path);
      out.push({ kind: 'header', node: n, collapsed: c });
      if (c) continue;
      if (n.children.length) walk(n.children);
      else for (const item of n.items) out.push({ kind: 'item', item, path: n.path });
    }
  };
  walk(nodes);
  return out;
}

export function toggleCollapsed(set: Set<string>, path: string): Set<string> {
  const next = new Set(set);
  if (next.has(path)) next.delete(path);
  else next.add(path);
  return next;
}

/** "Status › Stage" for the toggle chip. */
export function groupingSummary(groups: GroupKey[]): string {
  return groups.map((k) => t(GROUP_FIELD_LABEL[k])).join(' › ');
}
